import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { NAV_GROUPS } from "./navConfig";

const ROLE_LABEL: Record<string, string> = {
  ADMIN: "Administrador",
  FINANCEIRO: "Financeiro",
  SUPORTE: "Suporte",
  LEITURA: "Leitura",
};

function matchesPath(pathname: string, itemPath: string): boolean {
  return itemPath === "/app" ? pathname === "/app" : pathname.startsWith(itemPath);
}

// Menu lateral (layout "Razão"). Grupos com mais de um item começam fechados e abrem ao clicar
// (um por vez); o grupo da rota atual abre sozinho. `className` vem do AppShell: "collapsed" no
// desktop esconde o menu inteiro, "open" no celular mostra a gaveta (ver bootstrap-theme.css).
export function Sidebar({ className, onClose }: { className: string; onClose: () => void }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const [openGroup, setOpenGroup] = useState<string | null>(null);

  const groups = useMemo(
    () =>
      NAV_GROUPS.map((group) => ({
        ...group,
        items: group.items.filter((item) => !user || item.allow.includes(user.role)),
      })).filter((group) => group.items.length > 0),
    [user]
  );

  useEffect(() => {
    const active = groups.find((g) => g.items.length > 1 && g.items.some((item) => matchesPath(location.pathname, item.path)));
    if (active) setOpenGroup(active.title);
  }, [location.pathname, groups]);

  return (
    <aside className={"nt-sidebar " + className} aria-label="Menu principal">
      <div className="nt-sidebar-inner">
        <div className="nt-brand">
          <span className="nt-mark">S+</span>
          <span>
            <strong>Software Plus</strong>
            <small>Financeiro</small>
          </span>
          <button type="button" className="nt-side-close" aria-label="Fechar menu" onClick={onClose}>
            <i className="bi bi-x-lg" aria-hidden="true" />
          </button>
        </div>

        <nav className="nt-nav">
          {groups.map((group) => {
            if (group.items.length === 1) {
              const item = group.items[0];
              return (
                <NavLink key={item.path} to={item.path} end={item.path === "/app"} className={({ isActive }) => "nt-nav-item nt-nav-top" + (isActive ? " active" : "")}>
                  {item.label}
                </NavLink>
              );
            }
            const expanded = openGroup === group.title;
            const hasActive = group.items.some((item) => matchesPath(location.pathname, item.path));
            return (
              <div key={group.title} className="nt-nav-group">
                <button
                  type="button"
                  className={"nt-nav-toggle" + (hasActive ? " has-active" : "")}
                  aria-expanded={expanded}
                  onClick={() => setOpenGroup((cur) => (cur === group.title ? null : group.title))}
                >
                  <span>{group.title}</span>
                  <i className={`bi bi-chevron-${expanded ? "down" : "right"}`} aria-hidden="true" />
                </button>
                {expanded && (
                  <div className="nt-nav-items">
                    {group.items.map((item) => (
                      <NavLink key={item.path} to={item.path} className={({ isActive }) => "nt-nav-item" + (isActive ? " active" : "")}>
                        {item.label}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {user && (
          <div className="nt-me">
            <span className="nt-avatar">
              {user.name
                .split(" ")
                .map((p) => p[0])
                .slice(0, 2)
                .join("")
                .toUpperCase()}
            </span>
            <span className="min-w-0">
              <strong className="text-truncate">{user.name}</strong>
              <small>{ROLE_LABEL[user.role]}</small>
            </span>
            <button type="button" className="nt-me-out" onClick={() => logout()}>
              Sair
            </button>
          </div>
        )}
      </div>
    </aside>
  );
}
